import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useReducer,
} from "react"
import { GatsbyImage } from "gatsby-plugin-image"
import ReactPlayer from "react-player/lazy"
import {
  gtmVideoPlay,
  gtmVideoSeconds,
  gtmVideoPercentage,
} from "@components/Gtm"
import tw from "twin.macro"
import useMobileDetection from "@utils/useMobileDetection"

import MuteIcon from "@images/shared/icon_video_mute.inline.svg"
import UnMuteIcon from "@images/shared/icon_video_unmute.inline.svg"
import ReplayIcon from "@images/shared/icon_video_replay_hover.inline.svg"

const Video = forwardRef(
  (
    {
      videoId,
      videoSrc,
      title,
      poster,
      controls = true,
      autoplay,
      muted,
      loop,
      showMuteBtn,
    },
    ref
  ) => {
    const isMobile = useMobileDetection()

    const videoWrapperRef = useRef(null)

    const [pageLoaded, setPageLoaded] = useState(false)
    const [videoPlaying, setVideoPlaying] = useState(false)
    const [hidePlayButton, setHidePlayButton] = useState(false)
    const [videoEnded, setVideoEnded] = useState(false)
    const [videoMuted, setVideoMuted] = useState(muted || false)
    const [isScrolling, setIsScrolling] = useState(false)
    const [hasWindow, setHasWindow] = useState(false)
    const [, forceUpdate] = useReducer(x => x + 1, 0)

    useEffect(() => {
      setHasWindow(true)

      const handleResize = () => {
        forceUpdate()
      }

      window.addEventListener("resize", handleResize)
      return () => {
        window.removeEventListener("resize", handleResize)
      }
    }, [])

    const playVideo = () => {
      setVideoPlaying(true)
      setVideoEnded(false)
      videoWrapperRef.current?.focus()
      gtmVideoPlay(title)
    }

    const handleVideoEnded = () => {
      setVideoEnded(true)
      setHidePlayButton(false)
      setVideoPlaying(false)
    }

    const handleVideoProgress = progress => {
      const seconds = Math.round(progress.playedSeconds)
      const percentage = Math.round(progress.played * 100)
      let lastTriggeredTime = -1

      if (
        seconds !== 0 &&
        seconds % 10 === 0 &&
        seconds !== lastTriggeredTime
      ) {
        gtmVideoSeconds(title, seconds)
      }

      if (
        percentage !== 0 &&
        percentage % 10 === 0 &&
        percentage !== lastTriggeredTime
      ) {
        gtmVideoPercentage(title, percentage)
      }
    }

    useEffect(() => {
      setPageLoaded(true)
    }, [])

    useEffect(() => {
      if (autoplay && window.scrollY < 30) {
        playVideo()
      }
    }, [autoplay])

    useEffect(() => {
      const handleScroll = () => {
        if (!isMobile && autoplay) {
          if (window.scrollY > 30) {
            setVideoPlaying(false)
            setIsScrolling(false)
          } else {
            ref.current?.seekTo(0, "seconds")
            playVideo()
            setIsScrolling(true)
          }
        }
      }
      window.addEventListener("scroll", handleScroll)
      return () => {
        window.removeEventListener("scroll", handleScroll)
      }
    }, [isMobile, autoplay, ref])

    useEffect(() => {
      if (videoPlaying) {
        setHidePlayButton(true)
      } else {
        setHidePlayButton(false)
      }
    }, [videoPlaying])

    useEffect(() => {
      if (videoEnded) {
        setHidePlayButton(false)
        setVideoPlaying(false)
      }
    }, [videoEnded])

    return (
      <div data-id="hero-vimeo-player" tw="[iframe]:(relative z-[99])">
        <div ref={videoWrapperRef} tw="relative w-full aspect-[16/9]">
          <button
            data-id="play-video"
            css={[
              tw`absolute w-full h-full cursor-pointer visible opacity-100 z-[999] [svg circle]:(transition-all duration-300) [svg path]:(transition-all duration-300)
                        hover:([svg circle]:(fill-green transition-all duration-300) [svg path]:(fill-purple transition-all duration-300))`,
              hidePlayButton
                ? tw`invisible opacity-0 transition-all duration-500`
                : tw`visible opacity-100`,
            ]}
            onClick={() => playVideo()}
          >
            {poster && videoEnded ? (
              <GatsbyImage
                alt=""
                css={[
                  tw`!w-full !h-full absolute top-0 left-0 aspect-[16/9] opacity-0 invisible`,
                  pageLoaded && tw`opacity-100 visible`,
                ]}
                image={poster}
                objectFit="cover"
                objectPosition="top"
                className="bgImgRef"
              />
            ) : null}
          </button>

          <div
            css={[
              tw`w-full aspect-[16/9] opacity-0 invisible`,
              (hidePlayButton || isScrolling) &&
                tw`opacity-100 visible transition-all duration-500`,
            ]}
          >
            {hasWindow && (
              <ReactPlayer
                key={`${title}-${new Date().getTime()}`}
                data-id={videoId}
                ref={ref}
                controls={controls}
                playsinline={true}
                url={videoSrc}
                title={title}
                width="100%"
                height="100%"
                playing={videoPlaying}
                onProgress={progress => handleVideoProgress(progress, title)}
                muted={videoMuted}
                loop={loop}
                onEnded={handleVideoEnded}
                config={{
                  vimeo: {
                    // You can add Vimeo-specific options here if needed
                    playerOptions: {
                      muted: videoMuted,
                    },
                  },
                }}
              />
            )}
          </div>

          <div tw="absolute w-full h-full inset-0">
            <div tw="relative z-[999] top-4 max-w-[82rem] mx-auto bg-transparent w-full lg:(top-40 flex justify-end items-center px-4) 2xl:(px-0)">
              <button
                css={[
                  tw`absolute right-4 w-10 h-10 flex justify-end items-start [svg circle]:(transition-all duration-300) [svg path]:(transition-all duration-300) hover:([svg circle]:(fill-green transition-all duration-300) [svg path]:(fill-purple transition-all duration-300))`,
                  showMuteBtn && videoPlaying
                    ? tw`visible opacity-100 transition-all duration-500`
                    : tw`invisible opacity-0 `,
                ]}
                onClick={() => setVideoMuted(!videoMuted)}
              >
                {videoMuted ? (
                  <MuteIcon tw="w-8 h-8" />
                ) : (
                  <UnMuteIcon tw="w-8 h-8" />
                )}
              </button>

              <button
                css={[
                  tw`absolute right-4 text-green font-kanit text-base leading-5 font-bold tracking-[0.005rem] rounded-[6.25rem] py-2 uppercase flex justify-center items-center gap-2 [svg circle]:(transition-all duration-300) [svg path]:(transition-all duration-300) hover:([svg circle]:(fill-transparent transition-all duration-300) [svg path]:(fill-green transition-all duration-300))`,
                  videoEnded && !videoPlaying
                    ? tw`visible opacity-100 transition-all duration-500`
                    : tw`invisible opacity-0`,
                ]}
                onClick={() => playVideo()}
              >
                <ReplayIcon tw="w-6 h-6" />
                <span>Replay Video</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }
)

export default Video
